import { useState, useEffect, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Alert, AlertDescription } from "../components/ui/alert";
import { Progress } from "../components/ui/progress";
import { Loader2, CheckCircle2, Clock, AlertCircle, CreditCard, ExternalLink, LogOut, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import subscriptionService from "../services/subscriptionService";
import { subscriptionPrices } from "../config/subscriptionPrices";

export default function SubscriptionPage({ onLogout }) {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);

  const [actionLoading, setActionLoading] = useState(false);
  const [activating, setActivating] = useState(false);
  const pollingRef = useRef(null);

  useEffect(() => {
    const sessionId = searchParams.get('session_id');
    if (sessionId) {
      setActivating(true);
      confirmCheckout(sessionId);
    } else {
      loadStatus();
    }
  }, []);

  const confirmCheckout = async (sessionId) => {
    try {
      const data = await subscriptionService.confirmCheckout(sessionId);
      if (data.status === 'ACTIVE') {
        localStorage.setItem('subscriptionStatus', 'ACTIVE');
        if (data.companySize) localStorage.setItem('companySize', data.companySize);
        setStatus(data);
        setActivating(false);
        setLoading(false);
        toast.success("Payment successful! Your subscription is now active.");
        return;
      }
    } catch (error) {
      console.error('Confirm checkout failed, falling back to polling:', error);
    }
    pollUntilActive();
  };

  const pollUntilActive = async () => {
    const check = async () => {
      try {
        const data = await subscriptionService.getStatus();
        if (data.status === 'ACTIVE') {
          localStorage.setItem('subscriptionStatus', 'ACTIVE');
          if (data.companySize) localStorage.setItem('companySize', data.companySize);
          if (pollingRef.current) clearInterval(pollingRef.current);
          setStatus(data);
          setActivating(false);
          setLoading(false);
          toast.success("Payment successful! Your subscription is now active.");
          return true;
        }
      } catch {}
      return false;
    };

    const activated = await check();
    if (activated) return;

    let attempts = 0;
    pollingRef.current = setInterval(async () => {
      attempts++;
      const done = await check();
      if (done) {
        clearInterval(pollingRef.current);
        pollingRef.current = null;
      } else if (attempts >= 30) {
        clearInterval(pollingRef.current);
        pollingRef.current = null;
        setActivating(false);
        setLoading(false);
        setStatus({ plan: "PENDING", status: "EXPIRED" });
        toast.error("Subscription activation is taking longer than expected. Please contact support.");
      }
    }, 2000);
  };

  const loadStatus = async () => {
    try {
      const data = await subscriptionService.getStatus();
      setStatus(data);
      if (data.status) {
        localStorage.setItem('subscriptionStatus', data.status);
      }
      if (data.trialStart) {
        localStorage.setItem('trialStart', data.trialStart);
      }
      if (data.trialEnd) {
        localStorage.setItem('trialEnd', data.trialEnd);
      }
      if (data.daysRemaining !== undefined) {
        localStorage.setItem('daysRemaining', String(data.daysRemaining));
      }
      if (data.companySize) {
        localStorage.setItem('companySize', data.companySize);
      }
      if (data.orgOwnerEmail) {
        localStorage.setItem('orgOwnerEmail', data.orgOwnerEmail);
      }
    } catch {
      setStatus({ plan: "NONE", status: "NONE" });
    } finally {
      setLoading(false);
    }
  };

  const handleSubscribe = async () => {
    setActionLoading(true);
    try {
      const userRole = localStorage.getItem("userRole");
      const userPlan = localStorage.getItem("plan");
      let plan = "ADVISOR";
      if (userPlan === "ENTERPRISE") {
        plan = "ENTERPRISE";
      } else if (userRole === "ANALYST") {
        plan = "ANALYST";
      }
      const response = await subscriptionService.createCheckout(plan);
      if (response.url) {
        window.location.href = response.url;
      }
    } catch (error) {
      toast.error("Failed to start checkout");
    } finally {
      setActionLoading(false);
    }
  };

  const handlePortal = async () => {
    setActionLoading(true);
    try {
      const response = await subscriptionService.getPortalUrl();
      if (response.url) {
        window.location.href = response.url;
      }
    } catch (error) {
      toast.error("Failed to open billing portal");
    } finally {
      setActionLoading(false);
    }
  };

  const handleCancel = async () => {
    if (!confirm("Are you sure you want to cancel your subscription?")) return;
    setActionLoading(true);
    try {
      await subscriptionService.cancel();
      toast.success("Subscription cancelled");
      loadStatus();
    } catch (error) {
      toast.error("Failed to cancel subscription");
    } finally {
      setActionLoading(false);
    }
  };

  const goToDashboard = () => {
    const plan = localStorage.getItem('plan');
    navigate(plan === 'ENTERPRISE' ? '/admin' : '/dashboard');
  };

  if (activating) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardHeader className="text-center">
            <div className="flex justify-center mb-4">
              <Loader2 className="h-16 w-16 text-primary animate-spin" />
            </div>
            <CardTitle className="text-2xl">Activating Your Subscription</CardTitle>
            <CardDescription>
              Your payment was successful! We're activating your subscription now. This should only take a moment.
            </CardDescription>
          </CardHeader>
        </Card>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (status.orgOwnerEmail && status.status !== "ACTIVE" && status.status !== "COVERED_BY_ORG" && !((status.status === "TRIALING" || status.status === "CANCELED") && (status.daysRemaining ?? 0) > 0)) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardHeader className="text-center">
            <div className="flex justify-center mb-4">
              <AlertCircle className="h-16 w-16 text-amber-500" />
            </div>
            <CardTitle className="text-2xl">Subscription Requires Action</CardTitle>
            <CardDescription>
              Your organization's subscription needs to be renewed. Please contact your organization owner to subscribe.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <Alert>
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>
                Reach out to your organization owner at{" "}
                <a href={`mailto:${status.orgOwnerEmail}`} className="font-medium text-primary hover:underline">
                  {status.orgOwnerEmail}
                </a>{" "}
                to renew the subscription and restore access.
              </AlertDescription>
            </Alert>
            {onLogout && (
              <Button variant="outline" onClick={onLogout} className="w-full">
                <LogOut className="mr-2 h-4 w-4" /> Logout
              </Button>
            )}
          </CardContent>
        </Card>
      </div>
    );
  }

  if (status.status === "ACTIVE") {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardHeader className="text-center">
            <div className="flex justify-center mb-4">
              <CheckCircle2 className="h-16 w-16 text-green-500" />
            </div>
            <CardTitle className="text-2xl">Subscription Active</CardTitle>
            <CardDescription>
              Your subscription is now active. You have full access to all features.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Button onClick={goToDashboard} className="w-full" size="lg">
              Go to Dashboard <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (status.status === "NONE") {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardHeader className="text-center">
            <div className="flex justify-center mb-4">
              <AlertCircle className="h-16 w-16 text-amber-500" />
            </div>
            <CardTitle className="text-2xl">No Active Subscription</CardTitle>
            <CardDescription>Subscribe to a plan to access all features.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <Alert>
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>Your access has been restricted. Subscribe to continue using your account.</AlertDescription>
            </Alert>
            <Button onClick={handleSubscribe} disabled={actionLoading} className="w-full" size="lg">
              {actionLoading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <CreditCard className="mr-2 h-4 w-4" />}
              Subscribe Now
            </Button>
            {onLogout && (
              <Button variant="outline" onClick={onLogout} className="w-full">
                <LogOut className="mr-2 h-4 w-4" /> Logout
              </Button>
            )}
          </CardContent>
        </Card>
      </div>
    );
  }

  const daysLeft = status.daysRemaining ?? 0;
  const trialStartDate = status.trialStart ? new Date(status.trialStart) : null;
  const trialEndDate = status.trialEnd ? new Date(status.trialEnd) : null;
  const totalTrialDays = trialStartDate && trialEndDate
    ? Math.max(1, Math.round((trialEndDate - trialStartDate) / (1000 * 60 * 60 * 24)))
    : 30;
  const trialProgress = totalTrialDays > 0
    ? Math.min(100, ((totalTrialDays - daysLeft) / totalTrialDays) * 100)
    : 100;

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 p-4">
      <div className="max-w-2xl mx-auto py-8">
        <h1 className="text-3xl font-bold mb-8">Subscription</h1>

        <Card className="mb-6">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="text-xl">Current Plan</CardTitle>
                <CardDescription>Your subscription details</CardDescription>
              </div>
              <Badge className={status.status === "ACTIVE" ? "bg-green-500" : "bg-blue-500"}>
                {status.status}
              </Badge>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between p-4 bg-muted rounded-lg">
              <div>
                <p className="font-semibold text-lg">{status.plan}</p>
                <p className="text-sm text-muted-foreground">
                  {status.status === "TRIALING" ? "Trial" : status.status === "ACTIVE" ? "Active" : status.status === "EXPIRED" ? "Trial Ended" : status.status}
                  {status.plan === "ENTERPRISE" && status.companySize ? ` (${status.companySize} employees)` : ""}
                </p>
              </div>
              <p className="text-lg font-bold">
                {subscriptionPrices.getPlanPrice(status.plan, status.companySize)}
              </p>
            </div>

            {(status.status === "TRIALING" || status.status === "CANCELED" || status.status === "EXPIRED") && trialStartDate && trialEndDate && (
              <div className="space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Trial progress</span>
                  <span className={status.status === "EXPIRED" ? "text-red-500 font-medium" : ""}>
                    {status.status === "EXPIRED" ? "Expired" : `${daysLeft} days remaining`}
                  </span>
                </div>
                <Progress value={trialProgress} className={`h-2 ${status.status === "EXPIRED" ? "bg-red-200" : ""}`} />
                <div className="flex justify-between text-xs text-muted-foreground">
                  <span>{trialStartDate.toLocaleDateString()}</span>
                  <span>{trialEndDate.toLocaleDateString()}</span>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {status.plan === "ENTERPRISE" && (
          <Card className="mb-6">
            <CardContent className="pt-6">
              <div className="text-sm text-muted-foreground space-y-1">
                <p className="font-medium text-foreground">Contact us for custom pricing:</p>
                <p>
                  <a href="mailto:info@wealthbrainai.com" className="text-primary hover:underline font-medium">
                    info@wealthbrainai.com
                  </a>
                </p>
                <p>Phone: (203) 722-2138</p>
              </div>
            </CardContent>
          </Card>
        )}

        <Card className="mb-6">
          <CardHeader>
            <CardTitle>Manage</CardTitle>
            <CardDescription>Update your billing and subscription settings</CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            {status.status === "TRIALING" && daysLeft > 0 && (
              <>
                <Button onClick={handleSubscribe} disabled={actionLoading} variant="outline" className="w-full">
                  {actionLoading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <CreditCard className="mr-2 h-4 w-4" />}
                  Add Payment Method
                </Button>
                <Button onClick={() => { localStorage.setItem('trialSubscriptionSkipped', 'true'); navigate(localStorage.getItem('plan') === 'ENTERPRISE' ? '/admin' : '/dashboard'); }} className="w-full">
                  Skip for now <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </>
            )}
            {status.status === "TRIALING" && daysLeft <= 0 && (
              <Button onClick={handleSubscribe} disabled={actionLoading} variant="outline" className="w-full">
                {actionLoading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <CreditCard className="mr-2 h-4 w-4" />}
                Add Payment Method
              </Button>
            )}
            {status.status === "ACTIVE" && (
              <Button onClick={handlePortal} disabled={actionLoading} variant="outline" className="w-full">
                {actionLoading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <ExternalLink className="mr-2 h-4 w-4" />}
                Manage Billing (Stripe Portal)
              </Button>
            )}
            {status.status === "ACTIVE" && (
              <Button onClick={handleCancel} disabled={actionLoading} variant="ghost" className="w-full text-red-500 hover:text-red-600">
                Cancel Subscription
              </Button>
            )}
            {status.status === "CANCELED" && daysLeft > 0 && (
              <>
                <Button onClick={handleSubscribe} disabled={actionLoading} variant="default" className="w-full">
                  {actionLoading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <CreditCard className="mr-2 h-4 w-4" />}
                  Renew Subscription
                </Button>
                <Button onClick={() => { localStorage.setItem('trialSubscriptionSkipped', 'true'); navigate(localStorage.getItem('plan') === 'ENTERPRISE' ? '/admin' : '/dashboard'); }} className="w-full">
                  Skip for now <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </>
            )}
            {status.status === "CANCELED" && daysLeft <= 0 && (
              <div className="space-y-3">
                <Alert variant="destructive">
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>
                    Your trial period has expired. Please subscribe below to continue using all features.
                  </AlertDescription>
                </Alert>
                <Button onClick={handleSubscribe} disabled={actionLoading} className="w-full" size="lg">
                  {actionLoading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <CreditCard className="mr-2 h-4 w-4" />}
                  Subscribe Now
                </Button>
                <Button onClick={() => window.location.href = 'mailto:info@wealthbrainai.com'} variant="outline" className="w-full">
                  Contact Support for Extended Trial
                </Button>
              </div>
            )}
            {status.status === "EXPIRED" && (
              <div className="space-y-3">
                <Alert variant="destructive">
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>
                    Your trial period has expired. Please subscribe below to continue using all features.
                  </AlertDescription>
                </Alert>
                <Button onClick={handleSubscribe} disabled={actionLoading} className="w-full" size="lg">
                  {actionLoading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <CreditCard className="mr-2 h-4 w-4" />}
                  Subscribe Now
                </Button>
                <Button onClick={() => window.location.href = 'mailto:info@wealthbrainai.com'} variant="outline" className="w-full">
                  Contact Support for Extended Trial
                </Button>
              </div>
            )}
          </CardContent>
        </Card>

        {status.aiQuotaRemaining !== undefined && status.aiQuotaRemaining >= 0 && (
          <Card>
            <CardHeader>
              <CardTitle>AI Usage</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center justify-between p-3 bg-muted rounded-lg">
                <span className="text-sm">AI queries remaining this month</span>
                <span className="font-bold">{status.aiQuotaRemaining} / {status.aiQuotaTotal}</span>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}

function Badge({ children, className = "" }) {
  return (
    <span className={`px-3 py-1 rounded-full text-xs font-medium text-white ${className}`}>
      {children}
    </span>
  );
}
